import { ClearOutlined, CopyOutlined, ImportOutlined, LoadingOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Form, Input, Row, Select, Spin, Table, TableColumnsType, Typography } from "antd";
import { useGetConnectionSequenceStepsQuery } from "api/TenantSlug/ConnectionSequenceStep/hooks.ts";
import { useGetConversionStateQuery } from "api/TenantSlug/ConversionState/hooks.ts";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { RequestConnectionPropertyType } from "types/enums";

import RequestConnectionPropertyData = App.Data.RequestConnectionPropertyData;
import RequestHeaderConfigData = App.Data.RequestHeaderConfigData;
import RequestConfigData = App.Data.RequestConfigData;
import TestConnectionSequenceStepValuesRequest = App.Http.Requests.ConnectionSequenceStep.TestConnectionSequenceStepValuesRequest;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;

export interface BaseTestFormProps {
    formId: string;
    entityId: string;
    entityFilterKey: string;
    loadingMessage: string;
    errorMessage: string;
    stepValidationMessage: string;
    testMutation: {
        mutate: (params: any, options: { onSuccess: (response: any) => void; onError: (error: any) => void }) => void;
        isPending: boolean;
        isSuccess: boolean;
        isError: boolean;
        error?: { message?: string };
        reset: () => void;
    };
    onTestSuccess: (series: TestConnectionSequenceStepResponse["series"]) => ReactNode;
}

export const BaseTestForm: FC<BaseTestFormProps> = ({
    formId,
    entityId,
    entityFilterKey,
    loadingMessage,
    errorMessage,
    stepValidationMessage,
    testMutation,
    onTestSuccess,
}) => {
    const { t } = usePaldockTranslation("forms.base-test");
    const { data } = useGetConnectionSequenceStepsQuery({
        filter: {
            include:
                "requestConfig.requestConnectionPropertiesUrl,requestConfig.headers,requestConfig.requestConnectionProperties",
            filter: { [entityFilterKey]: entityId },
        },
    });
    const steps = data?.connectionSequenceSteps ?? [];

    const [firstStepId, setFirstStepId] = useState<string>("");
    const [queryValues, setQueryValues] = useState<Record<string, string>>({});
    const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
    const [bodyValues, setBodyValues] = useState<Record<string, string>>({});
    const [resultSeries, setResultSeries] = useState<TestConnectionSequenceStepResponse["series"] | null>(null);
    const [leadId, setLeadId] = useState<string | null>(null);
    const [conversionId, setConversionId] = useState<string | null>(null);

    const getConversionQuery = useGetConversionStateQuery(
        conversionId ?? "",
        {
            filter: { include: "country,owner,offer" },
        },
        {
            enabled: !!conversionId,
        }
    );

    useEffect(() => {
        setQueryValues({});
        setHeaderValues({});
        setBodyValues({});
    }, [firstStepId]);

    const step = steps.find((s) => s.id === firstStepId);
    const cfg: RequestConfigData | undefined = step?.requestConfig;

    useEffect(() => {
        getConversionQuery.refetch();
        setLeadId(null);
        setConversionId(null);
    }, [conversionId]);

    useEffect(() => {
        if (
            getConversionQuery.isSuccess &&
            getConversionQuery.data?.conversion?.formSubmissionValues &&
            cfg?.requestConnectionProperties
        ) {
            const allowed = new Set(cfg.requestConnectionProperties.map((p) => p.use_as_accessor));

            const map = getConversionQuery.data.conversion.formSubmissionValues.reduce<Record<string, string>>(
                (acc, { name, value }) => {
                    if (allowed.has(name)) {
                        acc[name] = value;
                    }
                    return acc;
                },
                {}
            );

            setBodyValues(map);
        }
    }, [getConversionQuery.isSuccess, getConversionQuery.data]);

    const handleSubmit = () => {
        const toArray = (map: Record<string, string>): TestConnectionSequenceStepValuesRequest[] =>
            Object.entries(map)
                .filter(([, value]) => value !== "")
                .map(([key, value]) => ({ key, value }));

        const payload = {
            first_step_id: firstStepId,
            query_values: toArray(queryValues),
            header_values: toArray(headerValues),
            body_values: toArray(bodyValues),
        };

        testMutation.mutate(
            payload,
            {
                onSuccess: (response) => {
                    setResultSeries(response.series);
                },
                onError: (error) => {
                    toast.error(`${errorMessage}: ${error.message}`);
                },
            }
        );
    };

    const resetTest = () => {
        testMutation.reset();
        setResultSeries([]);
    };

    if (testMutation.isPending) {
        return (
            <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                <Typography.Title level={4} style={{ marginTop: "1rem" }}>
                    {loadingMessage}
                </Typography.Title>
                <Typography.Paragraph type="secondary">{t("messages.please_wait_processing")}</Typography.Paragraph>
            </div>
        );
    }

    if (testMutation.isSuccess && resultSeries) {
        return onTestSuccess(resultSeries);
    }

    if (testMutation.isError) {
        return (
            <Alert
                type="error"
                showIcon
                message={errorMessage}
                description={
                    testMutation.error?.message
                        ? `Chybová zpráva: ${testMutation.error.message}`
                        : `Došlo k neznámé chybě při testování.`
                }
                action={
                    <Button size="large" onClick={() => resetTest()}>
                        {t("buttons.back_to_editor")}
                    </Button>
                }
            />
        );
    }

    const renderSection = (
        items: RequestConnectionPropertyData[] | undefined,
        valuesMap: Record<string, string>,
        setValuesMap: React.Dispatch<React.SetStateAction<Record<string, string>>>
    ) => {
        if (!items) return null;

        const copyAllExamples = () => {
            const all = items.reduce<Record<string, string>>((acc, item) => {
                acc[item.use_as_accessor] =
                    item.type === RequestConnectionPropertyType.Object ? item.accessor! : item.value!;
                return acc;
            }, {});
            setValuesMap(all);
        };

        const columns: TableColumnsType<RequestConnectionPropertyData> = [
            { title: "Key", dataIndex: "use_as_accessor", width: "30%", ellipsis: true },
            {
                title: (
                    <span>
                        <CopyOutlined
                            style={{ cursor: "pointer" }}
                            title="Copy all examples"
                            onClick={copyAllExamples}
                        />{" "}
                        {t("labels.example")}
                    </span>
                ),
                dataIndex: "value",
                render: (_, item) => {
                    const example = item.type === RequestConnectionPropertyType.Object ? item.accessor! : item.value!;

                    return (
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <CopyOutlined
                                style={{ cursor: "pointer" }}
                                onClick={() => setValuesMap((prev) => ({ ...prev, [item.use_as_accessor]: example }))}
                            />{" "}
                            <span>{example}</span>
                        </span>
                    );
                },
                width: "30%",
                ellipsis: true,
            },
            {
                title: (
                    <span>
                        <ClearOutlined
                            title="Vymazat všechny hodnoty"
                            style={{ cursor: "pointer" }}
                            onClick={() => setValuesMap({})}
                        />{" "}
                        {t("labels.value")}
                    </span>
                ),
                dataIndex: "use_as_accessor",
                render: (key: string) => (
                    <Input
                        value={valuesMap[key] || ""}
                        onChange={(e) => setValuesMap((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                ),
                width: "40%",
                ellipsis: true,
            },
        ];
        return (
            <Table
                dataSource={items}
                columns={columns}
                rowKey="use_as_accessor"
                pagination={false}
                rowClassName={(_, index) => (index % 2 === 0 ? "bg-[#FFFFFF]" : "bg-[#00000005]")}
            />
        );
    };

    const renderHeaders = (items: RequestHeaderConfigData[] | undefined) => {
        if (!items) return null;

        const copyAllExamples = () => {
            const all = items.reduce<Record<string, string>>((acc, item) => {
                acc[item.name] = item.value;
                return acc;
            }, {});
            setHeaderValues(all);
        };

        const columns: TableColumnsType<RequestHeaderConfigData> = [
            { title: "Key", dataIndex: "name", width: "30%", ellipsis: true },
            {
                title: (
                    <span>
                        <CopyOutlined
                            style={{ cursor: "pointer" }}
                            title="Copy all examples"
                            onClick={copyAllExamples}
                        />{" "}
                        {t("labels.example")}
                    </span>
                ),
                dataIndex: "value",
                render: (_, item) => {
                    const example = item.value;

                    return (
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <CopyOutlined
                                style={{ cursor: "pointer" }}
                                onClick={() => setHeaderValues((prev) => ({ ...prev, [item.name]: example }))}
                            />
                            <span>{example}</span>
                        </span>
                    );
                },
                width: "30%",
                ellipsis: true,
            },
            {
                title: (
                    <span>
                        <ClearOutlined
                            title="Vymazat všechny hlavičky"
                            style={{ cursor: "pointer" }}
                            onClick={() => setHeaderValues({})}
                        />{" "}
                        {t("labels.value")}
                    </span>
                ),
                dataIndex: "name",
                render: (name: string) => (
                    <Input
                        value={headerValues[name] || ""}
                        onChange={(e) => setHeaderValues((prev) => ({ ...prev, [name]: e.target.value }))}
                    />
                ),
                width: "40%",
                ellipsis: true,
            },
        ];
        return (
            <Table
                dataSource={items}
                columns={columns}
                rowKey="name"
                pagination={false}
                rowClassName={(_, index) => (index % 2 === 0 ? "bg-[#FFFFFF]" : "bg-[#00000005]")}
            />
        );
    };

    return (
        <Form id={formId} layout="vertical" onFinish={handleSubmit}>
            <DrawerCollapsible title="Settings">
                <Row gutter={16}>
                    <Col span={10}>
                        <Form.Item
                            name="first_step_id"
                            label="Initial step"
                            initialValue={firstStepId}
                            required={true}
                            rules={[{ required: true, message: stepValidationMessage }]}
                        >
                            <Select
                                value={firstStepId}
                                onChange={(val) => setFirstStepId(val)}
                                options={steps.map((s) => ({ label: s.name, value: s.id }))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                {step && (
                    <Row gutter={16} align="middle">
                        <Col span={14}>
                            <label htmlFor="leadId" style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                                {t("fields.lead_id.label")}
                            </label>
                            <div className={"flex items-center gap-x-2"}>
                                <Input
                                    id="leadId"
                                    placeholder="9f779654-ffe0-41c6-80e3-cf9ef2f342ec"
                                    value={leadId ?? ""}
                                    onChange={(e) => setLeadId(e.target.value)}
                                />
                                <Button
                                    type="default"
                                    loading={getConversionQuery.isFetching}
                                    onClick={() => {
                                        setConversionId(leadId);
                                    }}
                                >
                                    <ImportOutlined style={{ marginRight: 4 }} /> {t("buttons.import")}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </DrawerCollapsible>

            {step && (
                <DrawerCollapsible title="Query">
                    {renderSection(cfg?.requestConnectionPropertiesUrl, queryValues, setQueryValues)}
                </DrawerCollapsible>
            )}

            {step && <DrawerCollapsible title="Headers">{renderHeaders(cfg?.headers)}</DrawerCollapsible>}

            {step && cfg?.method.match(/^(POST|PUT|PATCH)$/i) && (
                <DrawerCollapsible title="Body">
                    {renderSection(cfg.requestConnectionProperties, bodyValues, setBodyValues)}
                </DrawerCollapsible>
            )}
        </Form>
    );
};
